import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import './ToastProvider.css';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

const getToastType = (message, explicitType) => {
  if (explicitType) return explicitType;
  const normalized = String(message || '').toLowerCase();
  if (normalized.includes('erro') || normalized.includes('error') || normalized.includes('inválido')) return 'error';
  if (normalized.includes('sucesso') || normalized.includes('enviado') || normalized.includes('copiado')) return 'success';
  if (normalized.includes('atenção') || normalized.includes('aviso') || normalized.includes('limite')) return 'warning';
  return 'info';
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message, options = {}) => {
    const text = typeof message === 'string' ? message : String(message || '');
    if (!text.trim()) return null;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const toast = {
      id,
      message: text,
      type: getToastType(text, options.type),
      duration: options.duration || 4500
    };

    setToasts(current => [...current, toast]);
    window.setTimeout(() => removeToast(id), toast.duration);
    return id;
  }, [removeToast]);

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message) => {
      showToast(message);
    };

    return () => {
      window.alert = originalAlert;
    };
  }, [showToast]);

  const value = useMemo(() => ({ showToast, removeToast }), [showToast, removeToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <p>{toast.message}</p>
            <button type="button" onClick={() => removeToast(toast.id)} aria-label="Fechar notificação">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
